import { Link, useLocation } from "react-router-dom";
import { Heart, Clock, AlertCircle, FileText, Calendar, ArrowLeft } from "lucide-react"
import { Helmet } from 'react-helmet';
import authService from '../../../Services/authService';
import React, { useEffect, useRef, useState } from 'react';
import { useCart } from "../../../Services/CartContext";
import { useNavigate } from "react-router-dom";
import QuantityControlButton from "../Order/QuantityControlButton";
import SEOSchema from '../../../utils/SEOSchema';



export default function TestDetailPage() {

  const location = useLocation(); // React Hook
  const { fetchCartCount } = useCart();
  const navigate = useNavigate();

  // Split the URL path
  let pathParts = location.pathname.split('/');
  // Get the category name from index 1
  let category_name = decodeURIComponent(pathParts[1]).replace(/-/g, ' ');
  // Get the test info from index 2
  let testData = pathParts[2];
  let categoryParts = testData.split('-');
  // Extract the test ID
  let test_id = categoryParts[categoryParts.length - 1];
  // Reconstruct the test name (without ID)
  let test_name = categoryParts.slice(0, -1).join(' ');
  //alert(category_name)
  const [testDetails, setTestDetails] = useState([]);
  const [relatedTests, setRelatedTests] = useState([]);
  const [quantity, setQuantity] = useState(1); // State for quantity
  const [cartItems, setCartItems] = useState([]);


  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to top when page loads
  }, [test_id]);

  useEffect(() => {
    loadTestsbyID();
    const user = authService.getCurrentuser();
    if (user) {
      loadCartItems();
    }
  }, [test_id]);


  const loadTestsbyID = () => {
    setTestDetails([]); // clear old data while new is loading
    setRelatedTests([]);
    const url = new URL(authService.getApiUrl() + 'testdetails/' + test_id);
    fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=UTF-8',
      },
    })
      .then((res) => {
        return res.json();
      })
      .then((result) => {
        setTestDetails(result.data.testdetails);
        setRelatedTests(result.data.related_tests);
      })
      .catch((err) => {
        console.log(err.message);
      });
  };

  const handleAddToCart = async (test_id, testinfo, ptype) => {
    const user = authService.getCurrentuser();
    if (!user) {
      navigate("/login");
      return;
    }

    const token = authService.getCurrentUsertoken();

    try {
      const res = await fetch(authService.getApiUrl() + "cart", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: user.id,
          type: ptype,
          name: testinfo.test_name || testinfo.package_name,
          test_id: test_id,
          quantity: 1,
          price: testinfo.price,
          overall_price: testinfo.price,
          preparation: testinfo.lab_test_preparation_description || '',
          organization_id: 1, // replace with dynamic ID if needed
          visit_type: testinfo.visit_type,

        }),
      });

      const data = await res.json();

      if (!res.ok) {
        console.error("API Error:", data);
        throw new Error("Failed to add to cart");
      }

      await fetchCartCount(); // Refresh cart count
    } catch (error) {
      console.error("Error adding to cart:", error.message);
    }
  };

  const loadCartItems = () => {
    const token = authService.getCurrentUsertoken();
    const url = new URL(authService.getApiUrl() + 'cartlist');
    fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        user_id: authService.getCurrentuser().id,
        organization_id: 1
      }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=UTF-8',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (res.status === 401) {
          authService.clientlogout();
          window.location.href = "/login";
        } else {
          return res.json();
        }
      })
      .then((result) => {
        const cartItems = result.data;
        setCartItems(cartItems);
      })
      .catch((err) => {
        console.log(err.message);
      });
  };

  const getQuantityForTest = (testId) => {
    const foundItem = cartItems.find((item) => item.test_id === testId);
    return foundItem ? foundItem.quantity : 0;
  };

  const handleBuyNow = () => {
    const user = authService.getCurrentuser();
    if (!user) {
      navigate("/login");
      return;
    }

    const token = authService.getCurrentUsertoken();
    const item = {
      user_id: user.id,
      type: "product",
      name: testDetails.test_name,
      test_id: testDetails.id,
      quantity: 1,
      price: testDetails.price,
      overall_price: testDetails.price,
      preparation: "",
      organization_id: 1,
      visit_type: testDetails.visit_type,
    };

    fetch(authService.getApiUrl() + "cart", {
      method: "POST",
      body: JSON.stringify(item),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=UTF-8",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then(() => {
        fetchCartCount(); // optional: update cart badge
        navigate("/cart"); // redirect after success
      })
      .catch((err) => {
        console.error("Error in Buy Now:", err);
      });
  };


  return (
    <>
      <Helmet>
        <title>{testDetails.test_name ? `${testDetails.test_name} - Pranaam365` : 'Test Details - Pranaam365'}</title>
        <meta name="description" content={testDetails.short_description || `Book ${testDetails.test_name || 'medical'} test online`} />
        <meta name="keywords" content={testDetails.keywords || 'lab test, diagnostic test, health screening, blood test, pathology, Pranaam365'} />
      </Helmet>
      
      {testDetails.test_name && (
        <SEOSchema 
          type="medicalTest"
          data={{
            name: testDetails.test_name,
            description: testDetails.short_description || '',
            url: window.location.href
          }}
        />
      )}
    
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Navigation */}


      {/* Breadcrumb */}
      <div className="bg-white border-b border-[#E0E0E0]">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-2">
          <div className="flex items-center space-x-2">
            <Link to="/" className="text-xs text-[#4A4A4A]/70 hover:text-[#951C25]">
              Home
            </Link>
            <span className="text-[#4A4A4A]/30">/</span>
            <Link to="#" onClick={() => navigate(-1)} className="text-xs text-[#4A4A4A]/70 hover:text-[#951C25]">
              {category_name}
            </Link>
            <span className="text-[#4A4A4A]/30">/</span>
            <span className="text-xs text-[#951C25]">{testDetails.test_name}</span>
          </div>
        </div>
      </div>

      {/* Test Header */}
      <section className="bg-[rgb(149,28,37,0.2)] py-6 md:py-8">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4 mb-4 md:mb-0">
              <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-white/50 backdrop-blur-sm flex items-center justify-center">
                <Heart size={32} className="text-[#951C25]" strokeWidth={1.5} fill="none" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-semibold text-[#951C25]">{testDetails.test_name}</h1>
                <p className="text-sm text-[#951C25]/80">{testDetails.short_description}</p>
              </div>
            </div>
            <div className="flex flex-col items-start md:items-end">
              <div className="text-2xl font-semibold text-[#951C25] mb-2">₹{testDetails.price} </div>
              {/* <button className="px-6 py-2 bg-[#951C25] text-white rounded-full text-sm font-medium hover:bg-[#951C25]/90 transition-colors" onClick={() => handleAddToCart( testDetails.id,testDetails, 'product')}>
                Add to Cart
              </button> */}
            </div>
          </div>
        </div>
      </section>

      {/* Test Details */}
      <section className="py-8 md:py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="md:col-span-2">
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-[#4A4A4A] mb-4">About this Test</h2>
                <div
                  className="text-[#4A4A4A]/80 leading-relaxed space-y-4"
                  dangerouslySetInnerHTML={{ __html: testDetails.description }}
                />
              </div>

              <div className="mb-8">
                <h2 className="text-xl font-semibold text-[#4A4A4A] mb-4">Preparation</h2>
                <div className="bg-[#F5F5F7] p-4 rounded-lg">
                  <div className="flex items-start">
                    <AlertCircle size={20} className="text-[#951C25] mt-0.5 mr-3 flex-shrink-0" />
                    <p className="text-[#4A4A4A]/80">{testDetails.preparation_description}</p>
                  </div>
                </div>
              </div>

              {/* <div className="mb-8">
                <h2 className="text-xl font-semibold text-[#4A4A4A] mb-4">Procedure</h2>
                <p className="text-[#4A4A4A]/80 leading-relaxed">{testDetails.procedure}</p>
              </div> */}
              {/* 
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-[#4A4A4A] mb-4">Results</h2>
                <p className="text-[#4A4A4A]/80 leading-relaxed mb-4">{testDetails.results}</p>

                <h3 className="text-lg font-medium text-[#4A4A4A] mb-3">Normal Ranges</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead className="bg-[#F5F5F7]">
                      <tr>
                        <th className="py-2 px-4 text-left text-sm font-medium text-[#4A4A4A]">Parameter</th>
                        <th className="py-2 px-4 text-left text-sm font-medium text-[#4A4A4A]">Normal Range</th>
                        <th className="py-2 px-4 text-left text-sm font-medium text-[#4A4A4A]">Unit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E0E0E0]">
                      {testDetails.normalRanges.map((item, index) => (
                        <tr key={index}>
                          <td className="py-2 px-4 text-sm text-[#4A4A4A]">{item.name}</td>
                          <td className="py-2 px-4 text-sm text-[#4A4A4A]">{item.range}</td>
                          <td className="py-2 px-4 text-sm text-[#4A4A4A]">{item.unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div> */}

              {/* <div className="mb-8">
                <h2 className="text-xl font-semibold text-[#4A4A4A] mb-4">Frequently Asked Questions</h2>
                <div className="space-y-4">
                  {testDetails.faqs.map((faq, index) => (
                    <div key={index} className="border border-[#E0E0E0] rounded-lg p-4">
                      <h3 className="text-base font-medium text-[#4A4A4A] mb-2">{faq.question}</h3>
                      <p className="text-sm text-[#4A4A4A]/80">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div> */}
            </div>

            {/* Sidebar */}
            <div className="md:col-span-1">
              <div className="bg-[#F5F5F7] rounded-lg p-6 sticky top-24">
                <h3 className="text-lg font-semibold text-[#4A4A4A] mb-4">Add to Cart this Test</h3>

                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-[#4A4A4A]/70">Price</span>
                    <span className="text-lg font-semibold text-[#951C25]">₹{testDetails.price} <span className="line-through text-gray-500 ml-1">₹{testDetails.mrp_price}</span></span>
                  </div>
                  <div className="h-px bg-[#E0E0E0] my-3"></div>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center">
                    <Clock size={16} className="text-[#951C25] mr-3" />
                    <span className="text-sm text-[#4A4A4A]">Results in 4-6 hours</span>
                  </div>
                  <div className="flex items-center">
                    <FileText size={16} className="text-[#951C25] mr-3" />
                    <span className="text-sm text-[#4A4A4A]">Digital reports</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar size={16} className="text-[#951C25] mr-3" />
                    <span className="text-sm text-[#4A4A4A]">Same day appointments</span>
                  </div>
                </div>

                {/* <QuantityControlButton 
                  initialQuantity={quantity} 
                  onChangeQuantity={setQuantity} 
                /> */}
                <div className="w-full">
                  <div className="w-full px-6 py-3 bg-[#951C25] text-white text-center rounded-full text-sm font-medium hover:bg-[#951C25]/90 transition-colors mb-3 flex justify-center items-center">
                    <QuantityControlButton
                      id={testDetails.id}
                      name={testDetails.test_name}
                      price={testDetails.price}
                      variant="simple"
                      visit_type={testDetails.visit_type}
                      quantity={getQuantityForTest(testDetails.id)}
                      className="text-white"  // Adding text-white here
                    />
                  </div>
                </div>

                <button className="w-full px-6 py-3 border border-[#951C25] text-[#951C25] rounded-full text-sm font-medium hover:bg-[#951C25]/10 transition-colors" onClick={handleBuyNow}>
                  Buy Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Tests */}
      <section className="py-8 md:py-12 bg-[#F5F5F7]">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl font-semibold text-[#4A4A4A] mb-6">Related Tests</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {relatedTests.map((test, index) => (
              <Link
                to={'/' + encodeURIComponent(category_name.replace(/ /g, '-')) + '/' + encodeURIComponent(test.test_name.replace(/ /g, '-')) + '-' + test.id}
                key={index}
                className="bg-white p-4 rounded-lg hover:shadow-sm transition-shadow border border-[#E0E0E0] hover:border-[#951C25]/30"
              >
                <h3 className="text-base font-medium text-[#4A4A4A] mb-2">{test.test_name}</h3>
                <p className="text-xs text-[#4A4A4A]/70 mb-3">{test.short_description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#951C25]">₹{test.price} <span className="line-through text-gray-500 ml-1">₹{test.mrp_price}</span></span>
                  <span className="text-xs text-[#951C25] font-medium hover:underline">View Details</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>


    </div>
    </>
  )
}
